import React, { useState, useRef, useCallback, useMemo } from 'react';
import useClickOutside from '@this/lib/hooks/useClickOutside';

interface Item {
  label: string;
  value: string;
}

interface PopupListSelectorProps {
  items: Item[];
  headerLabel: string;
  name: string;
  currentValue: string;
  onChange: (key: string) => void;
}

const PopupListSelector: React.FC<PopupListSelectorProps> = ({
  items,
  headerLabel,
  name,
  currentValue,
  onChange
}) => {
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const getCurrentLabel = useCallback((): string => {
    const item = items.find(i => i.value === currentValue);
    return item?.label ?? '';
  }, [items, currentValue]);

  const handleClickOutside = useCallback(() => {
    setShow(false);
  }, []);

  useClickOutside(ref, handleClickOutside, show);

  const toggleShowSelector = useCallback(() => {
    setShow(prev => !prev);
  }, []);

  const closeShowSelector = useCallback(() => {
    setShow(false);
  }, []);

  const handleChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      onChange(e.currentTarget.value);
      closeShowSelector();
    },
    [onChange, closeShowSelector]
  );

  const isFirstRadioButtonSelected = useMemo(() => {
    return items[0]?.value === currentValue;
  }, [items, currentValue]);

  const classNames = useMemo(() => {
    const baseClass = 'popup-list-selector';
    const buttonHiddenClass = `popup-list-selector${isFirstRadioButtonSelected ? '' : '__select'}`;

    return {
      base: baseClass,
      buttonShow: `${baseClass}__button-show`,
      buttonHidden: `${buttonHiddenClass}__button-hidden`,
      popup: `${baseClass}__popup`,
      popupHeader: `${baseClass}__popup__header`,
      popupCloseButton: `${baseClass}__popup__header__close-button`,
      popupBody: `${baseClass}__popup__body`,
      popupLabel: `${baseClass}__popup__body__label`
    };
  }, [isFirstRadioButtonSelected]);

  return (
    <div className={classNames.base} ref={ref}>
      {show ? (
        <>
          <button type="button" className={classNames.buttonShow} onClick={toggleShowSelector}>
            {getCurrentLabel()}
          </button>
          <div className={classNames.popup}>
            <div className={classNames.popupHeader}>
              {headerLabel}
              <button
                type="button"
                className={classNames.popupCloseButton}
                onClick={closeShowSelector}
                aria-label="閉じる"
              >
                ☓
              </button>
            </div>
            <div className={classNames.popupBody}>
              <ul>
                {items.map(item => (
                  <li key={item.value}>
                    <label htmlFor={`${name}-${item.value}`} className={classNames.popupLabel}>
                      <input
                        id={`${name}-${item.value}`}
                        type="radio"
                        name={name}
                        value={item.value}
                        onChange={handleChange}
                        checked={currentValue === item.value}
                      />
                      {item.label}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      ) : (
        <button type="button" className={classNames.buttonHidden} onClick={toggleShowSelector}>
          {getCurrentLabel()}
        </button>
      )}
    </div>
  );
};

export default PopupListSelector;
